// features/passwords/components/PersonaSelfTransferCells.jsx
import React, { useState } from "react";
import PersonaCheckModal from "../../user-dashboard/components/PersonaCheckModal";
import SelfTransferConfirmModal from "../../user-dashboard/components/SelfTransferConfirmModal";
import { getSessionStampWho, getSessionStampedAs } from "../../../api";

const RED_CELL = { backgroundColor: "#dc3545", color: "#fff" };
const GREEN_CELL = { backgroundColor: "#198754", color: "#fff" };

/**
 * Persona Check + Self Transfer table cells for advice password child rows.
 * Renders two <td>s plus modals.
 */
export default function PersonaSelfTransferCells({
  entry,
  onPersonaCheckSubmit,
  onPersonaCheckClear,
  onSelfTransferToggle,
}) {
  const [personaModal, setPersonaModal] = useState({ open: false, mode: "edit" });
  const [selfTransferOpen, setSelfTransferOpen] = useState(false);
  const [saving, setSaving] = useState({ persona: false, self: false });

  const sessionStampedAs = getSessionStampedAs();
  const sessionWho = getSessionStampWho();

  const personaChecked = !!entry?.persona_check_triggered;
  const selfChecked = !!entry?.self_manage_transfer;

  const handlePersonaChange = (checked) => {
    if (checked) {
      if (personaChecked) return;
      setPersonaModal({ open: true, mode: "edit" });
      return;
    }
    if (!onPersonaCheckClear) return;
    if (!window.confirm("Clear persona check for this email?")) return;
    setSaving((p) => ({ ...p, persona: true }));
    Promise.resolve(onPersonaCheckClear(entry))
      .catch((err) => console.error("Failed to clear persona check:", err))
      .finally(() => setSaving((p) => ({ ...p, persona: false })));
  };

  const handlePersonaSubmit = async ({ answers }) => {
    if (!onPersonaCheckSubmit) return;
    await onPersonaCheckSubmit(entry, { answers });
  };

  const handleSelfChange = (checked) => {
    if (!onSelfTransferToggle) return;
    if (checked) {
      if (selfChecked) return;
      setSelfTransferOpen(true);
      return;
    }
    if (!window.confirm("Disable Self TM Transfer for this email?")) return;
    setSaving((p) => ({ ...p, self: true }));
    Promise.resolve(onSelfTransferToggle(entry, false))
      .catch((err) => console.error("Failed to update self transfer:", err))
      .finally(() => setSaving((p) => ({ ...p, self: false })));
  };

  const handleSelfConfirm = async () => {
    setSaving((p) => ({ ...p, self: true }));
    try {
      await onSelfTransferToggle?.(entry, true);
    } finally {
      setSaving((p) => ({ ...p, self: false }));
    }
  };

  return (
    <>
      <td
        className="text-center align-middle"
        style={personaChecked ? RED_CELL : undefined}
        onClick={(e) => e.stopPropagation()}
      >
        <div className="d-flex flex-column align-items-center gap-1">
          <input
            type="checkbox"
            className="form-check-input m-0"
            checked={personaChecked}
            disabled={saving.persona || !onPersonaCheckSubmit}
            onChange={(e) => handlePersonaChange(e.target.checked)}
            style={{ cursor: onPersonaCheckSubmit ? "pointer" : "default" }}
          />
          {personaChecked && (
            <button
              type="button"
              className="btn btn-link btn-sm p-0"
              style={{
                fontSize: "0.7rem",
                color: "#fff",
                textDecoration: "underline",
              }}
              onClick={() => setPersonaModal({ open: true, mode: "view" })}
            >
              answers
            </button>
          )}
        </div>
      </td>

      <td
        className="text-center align-middle"
        style={selfChecked ? GREEN_CELL : undefined}
        onClick={(e) => e.stopPropagation()}
      >
        <div className="d-flex flex-column align-items-center gap-1">
          <input
            type="checkbox"
            className="form-check-input m-0"
            checked={selfChecked}
            disabled={saving.self || !onSelfTransferToggle}
            onChange={(e) => handleSelfChange(e.target.checked)}
            title={
              selfChecked && entry?.self_manage_transfer_since
                ? `Since ${new Date(entry.self_manage_transfer_since).toLocaleString()}`
                : "Self transfer"
            }
            style={{ cursor: onSelfTransferToggle ? "pointer" : "default" }}
          />
          {selfChecked && entry?.self_manage_transfer_since && (
            <span style={{ fontSize: "0.65rem", lineHeight: 1.1, opacity: 0.9 }}>
              {new Date(entry.self_manage_transfer_since).toLocaleDateString()}
            </span>
          )}
        </div>
      </td>

      <PersonaCheckModal
        open={personaModal.open}
        mode={personaModal.mode}
        record={entry}
        sessionStampedAs={sessionStampedAs}
        sessionWho={sessionWho}
        onClose={() => setPersonaModal({ open: false, mode: "edit" })}
        onSubmit={handlePersonaSubmit}
      />

      <SelfTransferConfirmModal
        open={selfTransferOpen}
        email={entry?.email}
        onClose={() => setSelfTransferOpen(false)}
        onConfirm={handleSelfConfirm}
      />
    </>
  );
}
